import React, { useState } from "react";
import { MOVING_COSTS } from "../renewalCopy";

interface Props {
  open: boolean;
  /** "I'm still moving out" — proceed to the next step. */
  onContinue: () => void;
  /** "I'd like to see renewal options" — switch flows. */
  onSeeRenewal: () => void;
}

/**
 * "Did You Know?" moving-cost breakdown, shown after the tenant picks a
 * move-out date and clicks Continue.
 *
 * It's a modal rather than inline content: on the page it was a wall of
 * two-tone text that loaded before the tenant had done anything, which made
 * the actual task (pick a date) hard to find. As a modal it arrives at the
 * decision point and requires an explicit choice to dismiss — there's no X
 * or backdrop close, matching "to move fwd, they need to click one of the
 * options".
 *
 * Rows are label-left / amount-right on a single line with a tight gap, so
 * the eye doesn't have to cross a wide gutter to pair them up.
 *
 * The hidden-costs list is collapsed behind a disclosure (same pattern as
 * "Tips for Avoiding Fees at Move-Out"): expanded, the modal ran long enough
 * that the two decision buttons fell below the fold, which is the one thing
 * that has to stay visible.
 */
const MovingCostsModal: React.FC<Props> = ({
  open,
  onContinue,
  onSeeRenewal,
}) => {
  const [hiddenOpen, setHiddenOpen] = useState(false);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
    >
      <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-xl bg-white shadow-lg">
        <div className="px-7 pb-5 pt-6">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted">
            {MOVING_COSTS.eyebrow}
          </p>
          <h4 className="mt-1 text-lg font-bold leading-snug text-primary">
            {MOVING_COSTS.title}
          </h4>
        </div>

        {/* Hard costs — the numbers are the point, so they carry the weight
            and the labels stay light. Generous row padding keeps the pairs
            scannable without needing dividers between every line. */}
        <div className="px-7">
          <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-muted">
            {MOVING_COSTS.hardCostsTitle.replace(/:$/, "")}
          </p>
          <dl className="divide-y divide-gray-100">
            {MOVING_COSTS.hardCosts.map((c) => (
              <div
                key={c.label}
                className="flex items-baseline justify-between gap-6 py-2.5"
              >
                <dt className="text-sm text-gray-600">{c.label}</dt>
                <dd className="whitespace-nowrap text-sm font-semibold tabular-nums text-primary">
                  {c.value}
                </dd>
              </div>
            ))}
          </dl>

          <p className="mt-5 rounded-lg bg-primary/5 px-4 py-3 text-sm font-semibold leading-relaxed text-primary">
            {MOVING_COSTS.summary}
          </p>
        </div>

        {/* Hidden costs — collapsed by default so the summary above and the
            buttons below stay on one screen. */}
        <div className="px-7 pt-4">
          <div className="overflow-hidden rounded-xl border border-gray-200">
            <button
              type="button"
              onClick={() => setHiddenOpen((v) => !v)}
              className="flex w-full cursor-pointer items-center justify-between gap-4 px-4 py-2.5 text-left transition-colors hover:bg-gray-50"
            >
              <span className="text-[11px] font-semibold uppercase tracking-wide text-primary">
                {MOVING_COSTS.hiddenCostsTitle.replace(/:$/, "")}
              </span>
              <span className="flex shrink-0 items-center gap-1 text-xs font-medium text-info">
                {hiddenOpen ? "Hide" : "See more"}
                <svg
                  className={`h-3.5 w-3.5 transition-transform ${
                    hiddenOpen ? "rotate-180" : ""
                  }`}
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2}
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </span>
            </button>

            {hiddenOpen && (
              <ul className="space-y-3 border-t border-gray-100 bg-gray-50/60 px-4 pb-3 pt-3">
                {MOVING_COSTS.hiddenCosts.map((c) => (
                  <li key={c.label}>
                    <p className="text-sm font-semibold text-primary">
                      {c.label}
                    </p>
                    <p className="mt-0.5 text-sm leading-relaxed text-gray-600">
                      {c.detail}
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <p className="mt-4 text-xs leading-relaxed text-muted">
            {MOVING_COSTS.disclaimer}
          </p>
        </div>

        {/* Amber, not grey: this is the modal's actual ask, and on the grey
            footer it read as boilerplate above the buttons. */}
        <div className="mt-5 border-t-2 border-amber-200 bg-amber-50 px-7 py-5">
          <p className="mb-4 text-sm font-medium leading-relaxed text-amber-900">
            {MOVING_COSTS.reconsider}
          </p>
          <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={onContinue}
              className="cursor-pointer rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100"
            >
              I'm still moving out
            </button>
            <button
              type="button"
              onClick={onSeeRenewal}
              className="cursor-pointer rounded-lg bg-primary px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-primary-light"
            >
              I'd like to see renewal options
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MovingCostsModal;
